/**
 * AiTransparencySection — explains Vellum's AI-disclosure approach with
 * three labeled tier cards (AI-assisted / AI-collaborated / AI-generated)
 * and a link to the full policy page.
 *
 * Copy is driven by the `landing.aiTransparency.*` i18n namespace. Each tier
 * is read via its own explicit key (tiers.assisted.label etc.) rather than a
 * single `returnObjects` call: per the comment in i18n/index.ts, a
 * `returnObjects` lookup that misses (because the target locale bundle hasn't
 * arrived yet) returns the raw key string and breaks `.map()` in the caller.
 * Three flat string lookups can each fall back to the eagerly-loaded English
 * bundle without that hazard.
 *
 * Emojis live in the component, not the locales. They're language-agnostic
 * visual anchors for the three tiers; moving them into locale files would add
 * maintenance cost without unlocking any real localisation win. If a future
 * locale ever needs to override them, the cleanest path is to add an optional
 * `emoji` key under each tier and have this component prefer it when present.
 */

import { useTranslation } from 'react-i18next';
import { TrustIcon, EditIcon, AiIcon } from '../../../../utils/icons';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';

import {
  Section,
  Container,
  SectionHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSub,
  BtnGhost,
} from '../../common/LandingPage.styled';

/** Tier slugs match the keys under `landing.aiTransparency.tiers.*`. */
const TIER_SLUGS = ['assisted', 'collaborated', 'generated'] as const;
type TierSlug = (typeof TIER_SLUGS)[number];

const TIER_ICON: Record<TierSlug, ComponentType<SvgIconProps>> = {
  assisted: TrustIcon,
  collaborated: EditIcon,
  generated: AiIcon,
};

export function AiTransparencySection() {
  const { t } = useTranslation();

  return (
    <Section
      style={{
        background: 'var(--color-bg-elevated)',
        borderTop: '1px solid var(--color-border-default)',
      }}
    >
      <Container>
        <SectionHeader>
          <SectionEyebrow>{t('landing.aiTransparency.eyebrow')}</SectionEyebrow>
          <SectionTitle style={{ fontSize: 28 }}>{t('landing.aiTransparency.title')}</SectionTitle>
          <SectionSub>{t('landing.aiTransparency.sub')}</SectionSub>
        </SectionHeader>

        <div
          style={{
            display: 'flex',
            gap: 24,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: 32,
          }}
        >
          {TIER_SLUGS.map((slug) => {
            const Icon = TIER_ICON[slug];
            return (
              <div
                key={slug}
                style={{
                  flex: '1 1 220px',
                  maxWidth: 260,
                  padding: '20px 24px',
                  border: '1px solid var(--color-border-default)',
                  borderRadius: 'var(--radius-lg)',
                  background: 'var(--color-bg-default)',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{ marginBottom: 8, color: 'var(--color-text-primary)' }}
                  aria-hidden="true"
                >
                  <Icon sx={{ fontSize: 32 }} />
                </div>
                <div
                  style={{ fontWeight: 600, marginBottom: 6, color: 'var(--color-text-primary)' }}
                >
                  {t(`landing.aiTransparency.tiers.${slug}.label`)}
                </div>
                <div
                  style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}
                >
                  {t(`landing.aiTransparency.tiers.${slug}.desc`)}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <BtnGhost to="/ai-transparency" style={{ display: 'inline-flex' }}>
            {t('landing.aiTransparency.learnMore')}
          </BtnGhost>
        </div>
      </Container>
    </Section>
  );
}

export default AiTransparencySection;
